<template>
  <div v-if="!userStore.user" class="absolute inset-0 bg-black bg-opacity-50 z-[100]">
    <div class="flex items-center justify-center h-full">
      <LayupSpinner class="w-16 h-16" />
    </div>
  </div>

  <!-- HEADER -->
  <div class="w-full border-b border-grey-light-200 bg-white fixed top-0 left-0 right-0 z-20">
    <div v-if="showBetaBanner" class="p-2 text-center w-full text-sm border-b border-grey-light-200 bg-brand-200">
      Welcome to our new dashboard! 🎉 Please
      <span @click="showSupportModal = true" class="font-medium text-brand-700 hover:cursor-pointer">let us know</span>
      what you think. The
      <a :href="oldDashboardUrl" target="_blank" rel="noopener noreferrer" class="font-medium text-brand-700 p-0"
        >old dashboard</a
      >
      is still available.
    </div>

    <NavbarComponent
      class="h-[72px]"
      @toggle-sidebar="toggleMenu"
      @show-profile="(e) => (showProfile = e)"
      @show-create-order-modal="showCreateOrderModal = true"
      @show-create-boost-rewards-modal="showCreateBoostRewardsModal = true"
      :is-menu-open="generalStore.isMenuOpen"
    />
  </div>

  <!-- FLEX LAYOUT -->
  <div
    class="flex min-h-screen bg-grey-light-50 transition-all duration-300"
    :class="[showBetaBanner ? 'pt-[109px]' : 'pt-[72px]']"
  >
    <!-- SIDEBAR -->
    <aside
      class="bg-white border-r border-grey-light-200 fixed z-30 transition-all duration-300 overflow-hidden"
      :style="{
        top: showBetaBanner ? '109px' : '72px',
        height: showBetaBanner ? 'calc(100vh - 109px)' : 'calc(100vh - 72px)',
        width: generalStore.isMenuOpen ? '300px' : '0',
      }"
    >
      <SideMenu
        @show-profile="(e) => (showProfile = e)"
        @show-contact-modal="showContactModal = true"
        @show-support-modal="showSupportModal = true"
        :is-menu-open="generalStore.isMenuOpen"
      />
    </aside>

    <!-- MAIN CONTENT -->
    <div
      class="flex-1 h-full overflow-auto transition-all duration-300 p-4"
      :style="{
        marginLeft: generalStore.isMenuOpen ? '300px' : '0',
      }"
    >
      <OtpModal
        :show-otp-screen="showOptScreen"
        :user="user"
        @update:show-otp-screen="(val) => (showOptScreen = val)"
      />
      <ProfileModal :show-profile="showProfile" @update:toggle-profile="(e) => (showProfile = e)" />
      <CreateOrderModal
        :show-create-order-modal="showCreateOrderModal"
        @update:show-create-order-modal="(val) => (showCreateOrderModal = val)"
      />
      <CreateBoostRewardsModal
        :show-create-boost-rewards-modal="showCreateBoostRewardsModal"
        @update:show-create-boost-rewards-modal="(val) => (showCreateBoostRewardsModal = val)"
      />
      <SupportTicketModal :show-modal="showSupportModal" @update:show-modal="(e: boolean) => (showSupportModal = e)" />
      <ContactUsModal :show-modal="showContactModal" @update:show-modal="(e: boolean) => (showContactModal = e)" />
      <router-view />
    </div>

    <!-- MOBILE OVERLAY -->
    <div
      v-if="generalStore.isMenuOpen"
      @click="toggleMenu"
      class="md:hidden fixed inset-0 bg-black bg-opacity-50 z-[5]"
    ></div>
  </div>
</template>

<script setup lang="ts">
import ContactUsModal from '@/components/common/ContactUsModal.vue';
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import OtpModal from '@/components/common/OtpModal.vue';
import ProfileModal from '@/components/common/ProfileModal.vue';
import SupportTicketModal from '@/components/common/SupportTicketModal.vue';
import NavbarComponent from '@/components/merchant/NavbarComponent.vue';
import CreateBoostRewardsModal from '@/components/merchant/orders/boost-rewards/CreateBoostRewardsModal.vue';
import CreateOrderModal from '@/components/merchant/orders/create-order-modal/CreateOrderModal.vue';
import SideMenu from '@/components/merchant/SideMenu.vue';
import { MERCHANT_UI_URL } from '@/config';
import { useGeneralStore } from '@/stores/general';
import { useMerchantStore } from '@/stores/merchants';
import { useUserStore } from '@/stores/users';
import { onMounted, ref } from 'vue';
import type { ModelUserResponse } from '@layup/api-sdk';

const generalStore = useGeneralStore();
const userStore = useUserStore();
const merchantStore = useMerchantStore();
const { user } = userStore;
const { merchant } = merchantStore;

const oldDashboardUrl = `${MERCHANT_UI_URL}/dashboard`;

const showCreateOrderModal = ref(false);
const showCreateBoostRewardsModal = ref(false);

const showOptScreen = ref(false);
const showProfile = ref(false);
const showBetaBanner = ref(true);
const showContactModal = ref(false);
const showSupportModal = ref(false);

onMounted(async () => {
  if (user) {
    if (user?.cellNumber && !user.verifiedMobile) {
      showOptScreen.value = true;
    } else if (!checkMerchantId(user)) {
      if (!merchantStore.activeMerchantId && user.merchantIds?.length) {
        merchantStore.onChangeMerchant(user.merchantIds[0]);
      }
    }
  }
});

const toggleMenu = (): void => {
  generalStore.isMenuOpen = !generalStore.isMenuOpen;
};

const checkMerchantId = (user: ModelUserResponse) => {
  const activeMerchantId = merchantStore.activeMerchantId;
  const isInvalidMerchantId = !user && (activeMerchantId == null || activeMerchantId === '');
  const isIncompleteOnboarding = merchant.onboardingState != null && merchant.onboardingState !== 'completed';
  const isContractNotSigned =
    merchant.merchantContractSigned != null &&
    (merchant.merchantContractSigned === 'pending' || merchant.merchantContractSigned !== 'complete & signed');

  return !(isInvalidMerchantId || isIncompleteOnboarding || isContractNotSigned);
};
</script>
